import React, {
  forwardRef,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { MODAL_TYPES } from '@components/modals';
import { useCan } from '@features/user';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  type Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useModal } from '@store/hooks';
import { debounce } from 'lodash';

import { useAppointmentTypes } from '../store/hooks';
import { type AppointmentType } from '../types';

import AppointmentTypeModal from './AppointmentTypeModal';
import { ColorDot } from './ColorDot';

interface AppointmentTypeOption {
  encounter_type_id: string;
  label: string;
  appointmentType: AppointmentType;
}

interface AppointmentTypePickerProps {
  // Controlled mode props
  value?: AppointmentType | null;
  onChange?: (appointmentType: AppointmentType | null) => void;
  inputValue?: string;
  onInputChange?: (value: string) => void;

  // Uncontrolled mode props (legacy)
  onAppointmentTypeSelect?: (appointmentType: AppointmentType | undefined) => void;
  initialAppointmentType?: AppointmentType;

  // Common props
  onOpen?: () => void;
  autoFocus?: boolean;
  nonDismissable?: boolean; // New prop to control dismissability
  size?: 'small' | 'medium'; // New prop to control input size
  providerId?: string; // For admin: fetch encounter types for this provider
  showManageButton?: boolean;
}

// Export a type for the ref
export interface AppointmentTypePickerRef {
  focusInput: () => void;
}

const setRefValue = <T,>(ref: React.Ref<T> | undefined, value: T | null) => {
  if (!ref) return;

  if (typeof ref === 'function') {
    ref(value);
    return;
  }

  (ref).current = value;
};

const AppointmentTypePicker = forwardRef<AppointmentTypePickerRef, AppointmentTypePickerProps>(
  (
    {
      // Controlled props
      value,
      onChange,
      inputValue: controlledInputValue,
      onInputChange: controlledOnInputChange,
      // Uncontrolled props
      onAppointmentTypeSelect,
      initialAppointmentType,
      // Common props
      onOpen,
      autoFocus = false,
      nonDismissable = false,
      size = 'medium',
      providerId,
      showManageButton = true,
    },
    ref,
  ) => {
    // Detect if component is being used in controlled mode
    const isControlled = value !== undefined || onChange !== undefined;
    const [options, setOptions] = useState<AppointmentTypeOption[]>([]);
    const [open, setOpen] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    // Internal state for uncontrolled mode
    const [uncontrolledSelectedAppointmentType, setUncontrolledSelectedAppointmentType] =
      useState<AppointmentTypeOption | null>(
        initialAppointmentType
          ? {
              encounter_type_id: initialAppointmentType.encounter_type_id,
              label: initialAppointmentType.encounter_type,
              appointmentType: initialAppointmentType,
            }
          : null,
      );
    const [uncontrolledInputValue, setUncontrolledInputValue] = useState(
      initialAppointmentType?.encounter_type || '',
    );

    // Use controlled values if provided, otherwise use internal state
    const effectiveValue = isControlled
      ? value
        ? {
            encounter_type_id: value.encounter_type_id,
            label: value.encounter_type,
            appointmentType: value,
          }
        : null
      : uncontrolledSelectedAppointmentType;

    const effectiveInputValue =
      controlledInputValue !== undefined ? controlledInputValue : uncontrolledInputValue;

    const [currentSearchText, setCurrentSearchText] = useState('');
    const canManageAppointmentTypes = useCan('manage_appointment_types');
    const { open: openModal } = useModal();

    // Use our custom hooks for API data
    const { appointmentTypes, isLoading, error } = useAppointmentTypes(
      currentSearchText,
      providerId,
    );

    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

    const autocompleteRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const handleOpenAppointmentTypesManager = useCallback(
      (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        openModal(MODAL_TYPES.APPOINTMENT_TYPES_MANAGEMENT, {
          source: 'appointment_type_picker',
          maxWidth: 'md',
          fullScreenOnMobile: true,
        });
      },
      [openModal],
    );

    // Expose the focusInput method through the ref
    useImperativeHandle(ref, () => ({
      focusInput: () => {
        const inputElement =
          inputRef.current ||
          (autocompleteRef.current?.querySelector('input') as HTMLInputElement | null);

        if (inputElement) {
          inputElement.focus();
        }
      },
    }));

    // Focus the input when autoFocus prop is true
    useEffect(() => {
      if (autoFocus) {
        setTimeout(() => {
          const inputElement =
            inputRef.current ||
            (autocompleteRef.current?.querySelector('input') as HTMLInputElement | null);

          if (inputElement) {
            inputElement.focus();
          }
        }, 100);
      }
    }, [autoFocus]);

    // Create debounced search function
    const debouncedSetSearchText = useCallback(
      debounce((searchTerm: string) => {
        setCurrentSearchText(searchTerm);
      }, 300),
      [],
    );

    // Update options when appointment types change
    useEffect(() => {
      if (!appointmentTypes) {
        setOptions([]);
        return;
      }

      const appointmentTypeOptions: AppointmentTypeOption[] = appointmentTypes.map(
        (appointmentType: AppointmentType) => ({
          encounter_type_id: appointmentType.encounter_type_id,
          label: appointmentType.encounter_type,
          appointmentType,
        }),
      );

      setOptions(appointmentTypeOptions);
    }, [appointmentTypes, currentSearchText]);

    useEffect(() => {
      if (!isControlled && initialAppointmentType) {
        setUncontrolledSelectedAppointmentType({
          encounter_type_id: initialAppointmentType.encounter_type_id,
          label: initialAppointmentType.encounter_type,
          appointmentType: initialAppointmentType,
        });
        setUncontrolledInputValue(initialAppointmentType.encounter_type);
      }
    }, [initialAppointmentType, isControlled]);

    const handleInputChange = (event: React.ChangeEvent<{}>, value: string, reason: string) => {
      // Update controlled or uncontrolled input value
      if (controlledOnInputChange) {
        controlledOnInputChange(value);
      } else {
        setUncontrolledInputValue(value);
      }

      if (reason === 'input') {
        // Cancel any pending debounced calls
        debouncedSetSearchText.cancel();

        if (value.trim() !== '') {
          // Use the debounced search
          debouncedSetSearchText(value);
        } else {
          // If the input is cleared, clear the search immediately
          setCurrentSearchText('');
        }
      }
    };

    const handleChange = (event: React.ChangeEvent<{}>, value: AppointmentTypeOption | null) => {
      if (value) {
        // Update value based on mode
        if (isControlled && onChange) {
          onChange(value.appointmentType);
        } else {
          setUncontrolledSelectedAppointmentType(value);
          if (onAppointmentTypeSelect) {
            onAppointmentTypeSelect(value.appointmentType);
          }
        }

        // Update input value
        if (controlledOnInputChange) {
          controlledOnInputChange(value.label);
        } else {
          setUncontrolledInputValue(value.label);
        }

        setCurrentSearchText('');
      } else {
        // Clear selection
        if (isControlled && onChange) {
          onChange(null);
        } else {
          setUncontrolledSelectedAppointmentType(null);
          if (onAppointmentTypeSelect) {
            onAppointmentTypeSelect(undefined);
          }
        }

        // Clear input value
        if (controlledOnInputChange) {
          controlledOnInputChange('');
        } else {
          setUncontrolledInputValue('');
        }

        setCurrentSearchText('');
      }
    };

    // Handle Enter key press to select the first option
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      // Prevent Escape key from closing when nonDismissable is true
      if (event.key === 'Escape' && nonDismissable && open) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (
        event.key === 'Enter' &&
        open &&
        options.length > 0 &&
        !event.shiftKey &&
        !event.ctrlKey
      ) {
        event.preventDefault();

        const firstOption = options[0];
        // Update based on mode
        if (isControlled && onChange) {
          onChange(firstOption.appointmentType);
        } else {
          setUncontrolledSelectedAppointmentType(firstOption);
          if (onAppointmentTypeSelect) {
            onAppointmentTypeSelect(firstOption.appointmentType);
          }
        }

        // Update input value
        if (controlledOnInputChange) {
          controlledOnInputChange(firstOption.label);
        } else {
          setUncontrolledInputValue(firstOption.label);
        }

        setCurrentSearchText('');

        setOpen(false);
      }
    };

    const handleAppointmentTypeUpdated = (updatedAppointmentType: AppointmentType) => {
      // Update the selected option with new data
      const updatedOption: AppointmentTypeOption = {
        encounter_type_id: updatedAppointmentType.encounter_type_id,
        label: updatedAppointmentType.encounter_type,
        appointmentType: updatedAppointmentType,
      };

      // Update based on mode
      if (isControlled && onChange) {
        onChange(updatedAppointmentType);
      } else {
        setUncontrolledSelectedAppointmentType(updatedOption);
        if (onAppointmentTypeSelect) {
          onAppointmentTypeSelect(updatedAppointmentType);
        }
      }

      // Update input value
      if (controlledOnInputChange) {
        controlledOnInputChange(updatedAppointmentType.encounter_type);
      } else {
        setUncontrolledInputValue(updatedAppointmentType.encounter_type);
      }
    };

    return (
      <>
        <Autocomplete
          id="appointment-type-picker"
          size={size}
          open={open}
          onOpen={() => {
            setOpen(true);
            if (onOpen) {
              onOpen();
            }
          }}
          onClose={(event, reason) => {
            // If nonDismissable is true, only allow closing when a selection is made
            if (nonDismissable && reason !== 'selectOption') {
              return;
            }
            setOpen(false);
            // Reset search when closing the picker to avoid stale filtered results
            if (currentSearchText) {
              setCurrentSearchText('');
              const labelToRestore = effectiveValue?.label || '';
              if (controlledOnInputChange) {
                controlledOnInputChange(labelToRestore);
              } else {
                setUncontrolledInputValue(labelToRestore);
              }
            }
          }}
          options={options}
          loading={isLoading}
          value={effectiveValue || null}
          inputValue={effectiveInputValue}
          onInputChange={handleInputChange}
          onChange={handleChange}
          getOptionLabel={option => option.label}
          ref={autocompleteRef}
          slotProps={{
            popper: {
              placement: 'bottom-start',
            },
          }}
          renderOption={(props, option, { index }) => (
            <Box component="li" {...props} key={option.encounter_type_id}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography sx={{ fontWeight: '500' }}>{option.label}</Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {index === 0 && (
                    <Chip
                      label="Enter"
                      size="small"
                      variant="outlined"
                      icon={<KeyboardReturnIcon fontSize="small" />}
                      sx={{
                        height: '20px',
                        mr: 1,
                        fontSize: '0.7rem',
                        '& .MuiChip-icon': {
                          fontSize: '0.9rem',
                          marginLeft: '4px',
                          marginRight: '-4px',
                        },
                      }}
                    />
                  )}

                  {option.appointmentType.default_duration && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        flexShrink: 0,
                        maxWidth: isMobile ? '60px' : 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {option.appointmentType.default_duration}min
                    </Typography>
                  )}

                  <ColorDot colorEnum={option.appointmentType.color} size={16} />
                </Box>
              </Box>
            </Box>
          )}
          renderInput={params => (
            <TextField
              {...params}
              label="Appointment Type"
              onKeyDown={handleKeyDown}
              error={!!error}
              helperText={error ? 'Failed to load appointment types' : undefined}
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  startAdornment: effectiveValue ? (
                    <InputAdornment position="start" sx={{ marginLeft: '6px' }}>
                      <ColorDot colorEnum={effectiveValue.appointmentType.color} size={16} />
                    </InputAdornment>
                  ) : null,
                  endAdornment: (
                    <>
                      {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.slotProps.input.endAdornment}
                      {showManageButton && canManageAppointmentTypes && (
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            size="small"
                            sx={{
                              fontSize: '0.875rem',
                              padding: '4px',
                              marginRight: '4px',
                            }}
                            onClick={handleOpenAppointmentTypesManager}
                            aria-label="Manage practice appointment types"
                            title="Manage practice appointment types"
                          >
                            <SettingsIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      )}
                      {effectiveValue && (
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            size="small"
                            sx={{
                              fontSize: '0.875rem',
                              padding: '4px',
                              marginRight: '4px',
                            }}
                            onClick={e => {
                              e.stopPropagation();
                              setModalOpen(true);
                            }}
                            aria-label="Edit appointment type"
                            title="Edit appointment type"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      )}
                    </>
                  ),
                },
                htmlInput: {
                  ...params.slotProps.htmlInput,
                  ref: (node: HTMLInputElement | null) => {
                    setRefValue(params.slotProps.htmlInput.ref, node);
                    inputRef.current = node;
                  },
                },
              }}
            />
          )}
          isOptionEqualToValue={(option, value) =>
            option?.encounter_type_id === value?.encounter_type_id
          }
          filterOptions={x => x}
          disableCloseOnSelect={nonDismissable}
          blurOnSelect={!nonDismissable}
          clearOnBlur={!nonDismissable}
          disableClearable={!nonDismissable}
        />

        <AppointmentTypeModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
          }}
          onSave={handleAppointmentTypeUpdated}
          type="edit"
          appointmentType={effectiveValue?.appointmentType || null}
          existingAppointmentTypes={appointmentTypes}
        />
      </>
    );
  },
);

export default AppointmentTypePicker;
